import React, { useEffect, useState } from "react";
import { apiClient } from "../services/authService";
import ExamenInteractivo from "./ExamenInteractivo";
import ExamenScrollView from "./ExamenScrollView";
import { Loading } from "./common/Loading";
import ResultadosExamenes from "./ResultadosExamenes";
import ReportesErroresEstudiante from "./ReportesErroresEstudiante";
import { useExamContext } from "../context/ExamContext";
import AvailableExamsDashboard from "./availableExams/AvailableExamsDashboard";
import "./ExamenList.css";

function ExamenList({ opcionSeleccionada: opcionSeleccionadaProp, onSelectOption: onSelectOptionProp }) {
  const [examenesPorEmpresa, setExamenesPorEmpresa] = useState([]);
  const [sesionActiva, setSesionActiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opcionSeleccionadaLocal, setOpcionSeleccionadaLocal] = useState(null);
  const [sesionDataPendiente, setSesionDataPendiente] = useState(null);
  const [showModalModo, setShowModalModo] = useState(false);
  const { setHideSidebar } = useExamContext();
  
  // Usar props si vienen del MainLayout, sino usar estado local
  const opcionSeleccionada = opcionSeleccionadaProp !== undefined ? opcionSeleccionadaProp : opcionSeleccionadaLocal;

  // Obtener exámenes agrupados por empresa
  useEffect(() => {
    const fetchExamenes = async () => {
      try {
        const res = await apiClient.get('/examenes/agrupados-por-empresa');
        setExamenesPorEmpresa(res.data);
      } catch (error) {
        console.error("Error loading exams:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchExamenes();
  }, []);

  // Ocultar/mostrar sidebar según si hay sesión activa o pantalla de selección de modo
  useEffect(() => {
    if (sesionActiva || showModalModo) {
      setHideSidebar(true);
    } else {
      setHideSidebar(false);
    }
  }, [sesionActiva, showModalModo, setHideSidebar]);

  // Resetear opcionSeleccionadaLocal cuando cambian las props (viene del MainLayout)
  useEffect(() => {
    if (opcionSeleccionadaProp !== undefined) {
      // Si recibimos una prop, usar esa
      // Si la prop es null, significa que debemos resetear
      if (opcionSeleccionadaProp === null) {
        setOpcionSeleccionadaLocal(null);
      }
    }
  }, [opcionSeleccionadaProp]);

  // --- LÓGICA DE "COMENZAR EXAMEN" ---
  const handleComenzarExamen = async (examenId) => {
    setLoading(true);
    setSesionDataPendiente(null);
    setShowModalModo(false);
    try {
      const res = await apiClient.get(`/examenes/${examenId}/iniciar`);
      if (!res.data || !res.data.sesion_id || !res.data.examen) {
        throw new Error("Invalid server response");
      }
      const data = res.data;
      setLoading(false);
      // Siempre mostrar modal de modo (nueva sesión siempre desde cero)
      setSesionDataPendiente(data);
      setShowModalModo(true);
    } catch (error) {
      console.error("Error starting session:", error);
      alert("Could not start exam. Please try again.");
      setLoading(false);
      setSesionActiva(null);
    }
  };

  const handleElegirModo = (modo) => {
    setSesionActiva({ ...sesionDataPendiente, modoExamen: modo });
    setShowModalModo(false);
    setSesionDataPendiente(null);
  };

  const handleBackToList = () => {
    setShowModalModo(false);
    setSesionDataPendiente(null);
    setHideSidebar(false); // Mostrar sidebar al volver a la lista
  };

  // Si estamos cargando la lista inicial O iniciando una sesión
  if (loading && !sesionActiva) {
    return (
      <div className="examen-list-wrapper">
        <Loading fullScreen={true} />
      </div>
    );
  }

  const handleSelectOption = (opcionId) => {
    if (onSelectOptionProp) {
      onSelectOptionProp(opcionId);
    } else {
      setOpcionSeleccionadaLocal(opcionId);
    }
  };

  // Renderizar el contenido según la opción seleccionada
  const renderContent = () => {
    if (opcionSeleccionada === 'resultados') {
      return <ResultadosExamenes />;
    } else if (opcionSeleccionada === 'reportes') {
      return <ReportesErroresEstudiante />;
    } else {
      // Dashboard de aprendizaje (Available Exams)
      return (
        <div className="examen-list-dashboard-root">
          <AvailableExamsDashboard
            grupos={examenesPorEmpresa}
            onStartExam={handleComenzarExamen}
          />
        </div>
      );
    }
  };

  // Si hay pantalla intermedia de selección de modo, mostrarla
  if (showModalModo && sesionDataPendiente) {
    return (
      <div className="examen-mode-selection-screen">
        <header className="examen-main-header">
          <img
            src="/logos/examcat_logo.png"
            alt="Examcat"
            className="brand-logo"
          />
          <div className="profile-icon">
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "#fff",
                color: "#0c4a6e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              U
            </div>
          </div>
        </header>

        <main className="examen-mode-selection-content">
          <h2 className="mode-selection-title">How do you want to take this exam?</h2>
          
          <div className="mode-selection-options">
            <button
              className="mode-option-card"
              onClick={() => handleElegirModo("real")}
            >
              <span className="mode-option-label">Real Simulation</span>
              <span className="mode-option-desc">Timed exam, no feedback until the end.</span>
            </button>
            <button
              className="mode-option-card"
              onClick={() => handleElegirModo("training")}
            >
              <span className="mode-option-label">Training Mode</span>
              <span className="mode-option-desc">Instant feedback with &quot;Check Answer&quot; on each question.</span>
            </button>
          </div>

          <button
            className="btn-back-to-list"
            onClick={handleBackToList}
          >
            Back to list
          </button>
        </main>

        <footer className="examen-main-footer" style={{ marginTop: "auto" }}>
          {/* Footer vacío para mantener consistencia visual */}
        </footer>
      </div>
    );
  }

  // Si NO hay sesión activa, muestra la lista
  if (!sesionActiva) {
    return (
      <div className="examen-list-wrapper">
        <div className="examen-list-content">
          {renderContent()}
        </div>
      </div>
    );
  }

  // Si SÍ hay una sesión activa, elige la vista según change_default_view
  const useScrollView =
    String(sesionActiva?.examen?.change_default_view || "").toLowerCase() === "yes";

  if (useScrollView) {
    return (
      <ExamenScrollView
        sesionData={sesionActiva}
        onVolver={() => setSesionActiva(null)}
      />
    );
  }

  return (
    <ExamenInteractivo
      sesionData={sesionActiva}
      onVolver={() => setSesionActiva(null)}
    />
  );
}

export default ExamenList;
